/**
 * PrivacyPage.tsx — /#/privacy
 */
import { useNavigate } from 'react-router-dom'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>{title}</h2>
    <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: 15 }}>{children}</div>
  </div>
)

export default function PrivacyPage() {
  const navigate = useNavigate()
  return (
    <div style={{ background: NAVY, color: '#e2e8f0', fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh' }}>
      <nav style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,168,76,0.15)', display: 'flex', alignItems: 'center', padding: '0 32px', height: 60, position: 'sticky', top: 0, zIndex: 100 }}>
        <div onClick={() => navigate('/')} style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1, cursor: 'pointer' }}>CASTELLAN</div>
        <span onClick={() => navigate('/login')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Sign in</span>
      </nav>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px' }}>
        <h1 style={{ fontSize: 40, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ color: '#475569', marginBottom: 48 }}>Last updated: May 10, 2026</p>

        <Section title="1. Who we are">
          Castellan Security is a security operations management platform operated by Asuelimen Ikhuoria. Our platform is accessible at castellan-security.com. Questions about this policy can be directed to support@castellan-security.com.
        </Section>

        <Section title="2. What data we collect">
          <p style={{ marginBottom: 12 }}>We collect the following data when you use Castellan:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Account data:</strong> Name, call sign, rank, employee number, email address, and phone number provided during registration.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Operational data:</strong> Shift assignments, post schedules, equipment assignments, and notes entered during shift operations.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Audit data:</strong> Login timestamps, actions taken, and device identifiers for security and compliance purposes.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Billing data:</strong> Billing is handled entirely by Stripe. We do not store credit card numbers or payment details on our servers.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Location data:</strong> On Terrain+NFC tier, officer location is captured at zone granularity only — no raw GPS coordinates are stored.</li>
          </ul>
        </Section>

        <Section title="3. How we use your data">
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}>To operate the platform and provide shift management features</li>
            <li style={{ marginBottom: 8 }}>To authenticate users and maintain session security</li>
            <li style={{ marginBottom: 8 }}>To send transactional emails (password resets, account approval, billing events)</li>
            <li style={{ marginBottom: 8 }}>To maintain an audit trail for compliance and accountability</li>
            <li style={{ marginBottom: 8 }}>To process subscription payments via Stripe</li>
          </ul>
          <p style={{ marginTop: 12 }}>We do not sell your data. We do not use your data for advertising. We do not share your data with third parties except as described in Section 4.</p>
        </Section>

        <Section title="4. Third-party services">
          <p style={{ marginBottom: 12 }}>We use the following third-party services:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Cloudflare</strong> — infrastructure, DNS, DDoS protection, and data storage (D1 database, KV store). Data is stored in Cloudflare's US-based data centers.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Stripe</strong> — payment processing and subscription management. Stripe's privacy policy applies to billing data.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Resend</strong> — transactional email delivery (password resets, welcome emails, billing notifications).</li>
          </ul>
        </Section>

        <Section title="5. Data retention">
          Operational shift data is retained for the duration of your subscription plus 30 days after cancellation. Audit logs are retained for 12 months. Account data is deleted within 30 days of account deletion upon request.
        </Section>

        <Section title="6. Your rights">
          <p style={{ marginBottom: 12 }}>Depending on your jurisdiction, you may have the right to:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}>Access the personal data we hold about you</li>
            <li style={{ marginBottom: 8 }}>Request correction of inaccurate data</li>
            <li style={{ marginBottom: 8 }}>Request deletion of your account and associated data</li>
            <li style={{ marginBottom: 8 }}>Object to processing of your data</li>
            <li style={{ marginBottom: 8 }}>Request a portable copy of your data</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise any of these rights, contact support@castellan-security.com.</p>
        </Section>

        <Section title="7. Security">
          All data is encrypted in transit via TLS. Passwords are hashed with a unique salt per user using SHA-256. JWT tokens are short-lived (15 minutes) and rotate on each use. We conduct periodic security reviews.
        </Section>

        <Section title="8. Children">
          Castellan is not intended for use by individuals under 18 years of age. We do not knowingly collect personal data from minors.
        </Section>

        <Section title="9. Changes to this policy">
          We may update this policy from time to time. We will notify registered users of material changes via email at least 14 days before they take effect. Continued use of the platform after that date constitutes acceptance.
        </Section>

        <Section title="10. Contact">
          Questions about this privacy policy should be directed to support@castellan-security.com.
        </Section>
      </div>
    </div>
  )
}
